import type React from 'react'

import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button, cn } from '@nextui-org/react'
import { Fragment, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

export const useDrawer = () => {
  const [isOpen, setIsOpen] = useState(false)

  function closeDrawer() {
    setIsOpen(false)
  }

  function openDrawer() {
    setIsOpen(true)
  }

  return { closeDrawer, isOpen, openDrawer }
}

type DrawerProps = {
  children: React.ReactNode
  hideCloseButton?: boolean
  isFullHeight?: boolean
  isFullWidth?: boolean
  isOpen: boolean
  onClose: () => void
  position?: 'bottom' | 'left' | 'right' | 'top'
}

type DrawerComponent = {
  Content: React.FC<PartialProps>
  Footer: React.FC<PartialProps>
  Header: React.FC<PartialProps>
} & React.FC<DrawerProps>

export const Drawer: DrawerComponent = ({
  children,
  hideCloseButton = false,
  isFullHeight,
  isFullWidth,
  isOpen,
  onClose,
  position = 'right',
}: DrawerProps) => {
  let enterFrom, leaveTo
  switch (position) {
    case 'bottom':
      enterFrom = 'translate-y-full'
      leaveTo = 'translate-y-full'
      break
    case 'left':
      enterFrom = '-translate-x-full'
      leaveTo = '-translate-x-full'
      break
    case 'top':
      enterFrom = '-translate-y-full'
      leaveTo = '-translate-y-full'
      break
    case 'right':
    default:
      enterFrom = 'translate-x-full'
      leaveTo = 'translate-x-full'
      break
  }

  return (
    <Transition as={Fragment} show={isOpen}>
      <Dialog as="div" className="relative z-[1023]" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-primary/60 transition-opacity" />
        </TransitionChild>
        <div
          className={cn(`fixed inset-0 z-[1024] flex`, {
            'justify-center': position === 'top' || position === 'bottom',
            'justify-end': position === 'right',
            'justify-start': position === 'left',
          })}
        >
          <TransitionChild
            as={Fragment}
            enter="transform transition ease-in-out duration-300"
            enterFrom={enterFrom}
            enterTo="translate-x-0"
            leave="transform transition ease-in-out duration-300"
            leaveFrom="translate-x-0"
            leaveTo={leaveTo}
          >
            <DialogPanel
              className={cn(
                `relative flex w-screen flex-col overflow-hidden bg-white shadow-2xl`,
                {
                  'h-[100dvh]': isFullHeight,
                  'h-fit max-h-[90vh] min-h-[60vh] w-full':
                    position === 'top' || position === 'bottom',
                  'h-full max-w-[90%] md:max-w-2xl':
                    position === 'right' || position === 'left',
                  'w-full max-w-full': isFullWidth,
                },
              )}
            >
              {!hideCloseButton && (
                <div className="absolute right-5 top-4">
                  <Button
                    className="text-foreground-500"
                    isIconOnly
                    onClick={onClose}
                    type="button"
                    variant="light"
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" />
                  </Button>
                </div>
              )}
              {children}
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}

type PartialProps = {
  children: React.ReactNode
  className?: string
} & React.HTMLAttributes<HTMLDivElement>

const Header: React.FC<PartialProps> = ({
  children,
  className,
  ...props
}: PartialProps) => (
  <div
    className={cn(
      `flex h-24 flex-shrink-0 items-center justify-between px-10 ${className ?? ''}`,
    )}
    {...props}
  >
    {children}
  </div>
)

const Content: React.FC<PartialProps> = ({
  children,
  className,
  ...props
}: PartialProps) => (
  <div className={cn(`flex-1 px-10 ${className ?? ''}`)} {...props}>
    {children}
  </div>
)

const Footer: React.FC<PartialProps> = ({
  children,
  className,
  ...props
}: PartialProps) => (
  <div
    className={cn(
      `h-[17rem] flex-shrink-0 border-t border-gray-200 ${className ?? ''}`,
    )}
    {...props}
  >
    <div className="px-10 pt-10">{children}</div>
  </div>
)

Drawer.Header = Header
Drawer.Content = Content
Drawer.Footer = Footer

export default Drawer
